// 判断是否需要转码（WAV 或 MP4）
export function needsConversion(file: File): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext === 'wav' || ext === 'mp4';
}

let ffmpegInstance: any = null;
let loadPromise: Promise<any> | null = null;

// 动态加载 FFmpeg WASM（单线程 UMD 版本，无需 SharedArrayBuffer）
async function loadFFmpeg(onProgress?: (msg: string) => void): Promise<any> {
  if (ffmpegInstance?.loaded) return ffmpegInstance;

  if (loadPromise) {
    await loadPromise;
    return ffmpegInstance;
  }

  onProgress?.('Loading converter...');

  loadPromise = (async () => {
    // 动态 import @ffmpeg/ffmpeg（已安装的 npm 包）
    const { FFmpeg } = await import('@ffmpeg/ffmpeg');
    const { toBlobURL } = await import('@ffmpeg/util');

    const ff = new FFmpeg();

    // 使用 UMD 单线程版本，兼容无 SharedArrayBuffer 环境
    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
    await ff.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });

    ffmpegInstance = ff;
  })();

  try {
    await loadPromise;
  } catch (e) {
    loadPromise = null;
    ffmpegInstance = null;
    throw e;
  }

  return ffmpegInstance;
}

// 将 WAV/MP4 转为 MP3，返回新的 File 对象
export async function convertToMp3(
  file: File,
  onProgress?: (msg: string) => void
): Promise<File> {
  const ff = await loadFFmpeg(onProgress);
  const { fetchFile } = await import('@ffmpeg/util');

  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const inputName = `input.${ext}`;
  const outputName = 'output.mp3';

  onProgress?.('Converting to MP3...');

  // 写入输入文件
  await ff.writeFile(inputName, await fetchFile(file));

  // 转码：128kbps MP3
  await ff.exec(['-i', inputName, '-vn', '-ab', '128k', '-ar', '44100', '-f', 'mp3', outputName]);

  // 读取输出
  const data = await ff.readFile(outputName);

  // 清理
  await ff.deleteFile(inputName);
  await ff.deleteFile(outputName);

  // 构造新 File，文件名改为 .mp3
  const newName = file.name.replace(/\.(wav|mp4)$/i, '.mp3');
  return new File([data], newName, { type: 'audio/mpeg' });
}
